import { cognitoConfigured, cognitoUrl, environment } from './env'
import type { AuthTokens, Role, Session, SessionUser } from '../types'

const SESSION_KEY = 'qte.session'
const PKCE_VERIFIER_KEY = 'qte.auth.pkce-verifier'
const AUTH_STATE_KEY = 'qte.auth.state'

const base64Url = (bytes: Uint8Array) =>
  btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '')

const randomValue = () => {
  const bytes = new Uint8Array(32)
  crypto.getRandomValues(bytes)
  return base64Url(bytes)
}

const sha256 = async (value: string) => {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(value))
  return base64Url(new Uint8Array(digest))
}

const decodeJwt = (token: string): Record<string, unknown> => {
  const [, payload] = token.split('.')
  if (!payload) throw new Error('The identity provider returned an invalid token.')
  const normalized = payload.replace(/-/g, '+').replace(/_/g, '/')
  const padded = normalized.padEnd(normalized.length + ((4 - (normalized.length % 4)) % 4), '=')
  return JSON.parse(atob(padded)) as Record<string, unknown>
}

const getRoles = (claims: Record<string, unknown>): Role[] => {
  const groups = claims['cognito:groups']
  if (!Array.isArray(groups)) return []
  return groups.filter((group): group is Role => group === 'Employee' || group === 'Manager' || group === 'Admin')
}

const userFromTokens = (tokens: AuthTokens): SessionUser => {
  const claims = decodeJwt(tokens.idToken || tokens.accessToken)
  return {
    email: typeof claims.email === 'string' ? claims.email : 'Signed-in user',
    name:
      (typeof claims.name === 'string' && claims.name) ||
      (typeof claims.preferred_username === 'string' && claims.preferred_username) ||
      'Signed-in user',
    roles: getRoles(claims),
    subject: typeof claims.sub === 'string' ? claims.sub : 'unknown'
  }
}

export const getStoredSession = (): Session | null => {
  const raw = sessionStorage.getItem(SESSION_KEY)
  if (!raw) return null

  try {
    const session = JSON.parse(raw) as Session
    if (!session.isDemo && (!session.tokens || session.tokens.expiresAt <= Date.now())) {
      clearSession()
      return null
    }
    return session
  } catch {
    clearSession()
    return null
  }
}

export const saveSession = (session: Session) => sessionStorage.setItem(SESSION_KEY, JSON.stringify(session))

export const clearSession = () => {
  sessionStorage.removeItem(SESSION_KEY)
  sessionStorage.removeItem(PKCE_VERIFIER_KEY)
  sessionStorage.removeItem(AUTH_STATE_KEY)
}

export const beginHostedUiSignIn = async () => {
  if (!cognitoConfigured) throw new Error('Cognito sign-in is not configured for this environment.')

  const verifier = randomValue()
  const state = randomValue()
  sessionStorage.setItem(PKCE_VERIFIER_KEY, verifier)
  sessionStorage.setItem(AUTH_STATE_KEY, state)

  const params = new URLSearchParams({
    response_type: 'code',
    client_id: environment.cognitoClientId,
    redirect_uri: environment.cognitoRedirectUri,
    scope: environment.cognitoScopes,
    code_challenge: await sha256(verifier),
    code_challenge_method: 'S256',
    state
  })
  window.location.assign(cognitoUrl(`/oauth2/authorize?${params.toString()}`))
}

export const completeHostedUiSignIn = async (search: string): Promise<Session> => {
  const params = new URLSearchParams(search)
  const providerError = params.get('error_description') || params.get('error')
  if (providerError) throw new Error(providerError)

  const code = params.get('code')
  const receivedState = params.get('state')
  const expectedState = sessionStorage.getItem(AUTH_STATE_KEY)
  const verifier = sessionStorage.getItem(PKCE_VERIFIER_KEY)
  if (!code || !receivedState || !expectedState || receivedState !== expectedState || !verifier) {
    throw new Error('Your sign-in session could not be verified. Please start again.')
  }

  const response = await fetch(cognitoUrl('/oauth2/token'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: environment.cognitoClientId,
      code,
      redirect_uri: environment.cognitoRedirectUri,
      code_verifier: verifier
    })
  })
  if (!response.ok) throw new Error('The sign-in token exchange was not successful. Please try again.')

  const result = (await response.json()) as {
    access_token: string
    id_token?: string
    expires_in?: number
  }
  const tokens: AuthTokens = {
    accessToken: result.access_token,
    idToken: result.id_token,
    expiresAt: Date.now() + (result.expires_in || 3600) * 1000
  }
  const session: Session = { isDemo: false, tokens, user: userFromTokens(tokens) }
  saveSession(session)
  sessionStorage.removeItem(PKCE_VERIFIER_KEY)
  sessionStorage.removeItem(AUTH_STATE_KEY)
  return session
}

export const createDemoSession = (role: Role): Session => {
  const roles: Role[] =
    role === 'Admin' ? ['Employee', 'Manager', 'Admin'] : role === 'Manager' ? ['Employee', 'Manager'] : ['Employee']
  const session: Session = {
    isDemo: true,
    user: {
      email: role === 'Admin' ? 'alex.morgan@quantuminsights.us' : 'jordan.lee@quantuminsights.us',
      name: role === 'Admin' ? 'Alex Morgan' : 'Jordan Lee',
      roles,
      subject: `demo-${role.toLowerCase()}`
    }
  }
  saveSession(session)
  return session
}

export const hostedUiLogout = () => {
  clearSession()
  if (!cognitoConfigured) {
    window.location.assign(environment.appBasePath)
    return
  }

  const params = new URLSearchParams({
    client_id: environment.cognitoClientId,
    logout_uri: environment.cognitoLogoutUri
  })
  window.location.assign(cognitoUrl(`/logout?${params.toString()}`))
}
